import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, MessageCircle, X, ArrowDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GroupChat = ({ groupId, unreadCount = 0, onRead }) => {
  const { user, token, getAuthHeaders } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      fetchMessages();
      connectWebSocket();
    }
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [open, groupId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ajo/groups/${groupId}/chat`, {
        headers: getAuthHeaders(),
      });
      setMessages(res.data.messages || []);
      if (onRead) onRead(groupId);
    } catch (e) {
      console.error('Failed to load messages');
    }
  };

  const connectWebSocket = () => {
    if (wsRef.current) wsRef.current.close();
    try {
      const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const ws = new WebSocket(`${wsUrl}/api/ws/chat/${groupId}?token=${token}`);
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message') {
          setMessages((prev) => {
            if (prev.some((m) => m.id === data.data.id)) return prev;
            return [...prev, data.data];
          });
        }
      };
      ws.onerror = () => {};
      ws.onclose = () => {};
      wsRef.current = ws;
    } catch (e) {
      console.error('WebSocket connection failed');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ajo/groups/${groupId}/chat/send`,
        { message: newMessage.trim() },
        { headers: getAuthHeaders() }
      );
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === res.data.id)) return prev;
          return [...prev, res.data];
        });
      }
      setNewMessage('');
    } catch (e) {
      console.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 300);
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDay = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Today';
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
  };

  // Floating toggle button
  if (!open) {
    return (
      <button
        onClick={handleOpen}
        className="fixed bottom-24 right-4 z-50 p-4 rounded-full bg-[#0B1F3A] text-white shadow-xl hover:scale-105 transition-transform"
        data-testid="chat-toggle-btn"
      >
        <MessageCircle size={24} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1" data-testid="chat-unread-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
    );
  }

  let lastDay = '';

  // Full-screen chat panel on mobile
  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-white" data-testid="group-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#0B1F3A] safe-area-top flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageCircle size={18} className="text-white" />
          <span className="font-bold text-white text-sm">Group Chat</span>
          <span className="text-xs text-white/60">{messages.length} messages</span>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="p-2 rounded-full hover:bg-white/20 transition"
          data-testid="chat-close-btn"
        >
          <X size={20} className="text-white" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1" data-testid="chat-messages-list">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <MessageCircle size={40} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-400">No messages yet. Say hi!</p>
            </div>
          </div>
        )}
        {messages.map((msg) => {
          const isMe = msg.user_id === user?.id;
          const day = formatDay(msg.created_at);
          let showDay = false;
          if (day !== lastDay) {
            showDay = true;
            lastDay = day;
          }
          return (
            <div key={msg.id}>
              {showDay && (
                <div className="text-center my-3">
                  <span className="text-[10px] bg-slate-100 text-slate-500 px-3 py-1 rounded-full">
                    {day}
                  </span>
                </div>
              )}
              <div className={`flex ${isMe ? 'justify-end' : 'justify-start'} mb-1`}>
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-2xl ${
                    isMe
                      ? 'bg-[#0B1F3A] text-white rounded-br-md'
                      : 'bg-slate-100 text-slate-800 rounded-bl-md'
                  }`}
                >
                  {!isMe && (
                    <p className="text-[10px] font-bold text-indigo-600 mb-0.5">{msg.user_name}</p>
                  )}
                  <p className="text-sm leading-snug">{msg.message}</p>
                  <p className={`text-[9px] mt-0.5 ${isMe ? 'text-white/50' : 'text-slate-400'}`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input - safe area bottom for mobile */}
      <form onSubmit={handleSend} className="p-3 border-t bg-white flex gap-2 safe-area-bottom flex-shrink-0">
        <Input
          ref={inputRef}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 h-11 rounded-full text-sm bg-slate-50"
          maxLength={1000}
          data-testid="chat-input"
        />
        <Button
          type="submit"
          disabled={!newMessage.trim() || sending}
          className="h-11 w-11 rounded-full bg-[#0B1F3A] hover:bg-[#1a3080] text-white p-0 flex-shrink-0"
          data-testid="chat-send-btn"
        >
          <Send size={16} />
        </Button>
      </form>
    </div>
  );
};

export default GroupChat;
